/**
 * Cesium base-imagery readiness watcher (#259) — fires ONCE when the globe's
 * base-imagery tile queue has genuinely drained for the current view, so the
 * cold-boot loading chrome (GlobeBootOverlay) fades on REAL readiness, never a
 * fixed timer. (The ~30s "black globe on cold boot" is Ion world-imagery tiles
 * streaming; this is the signal that they have arrived.)
 *
 * Why a dedicated watcher (mirrors cesiumOcclusionPump / cesiumRenderGuard —
 * structural viewer slice + injectable deps so it unit-tests without real Cesium):
 *   - `globe.tilesLoaded` is the authoritative "queue drained" flag, but it reads
 *     TRUE at frame 0 BEFORE any tiles are even queued (an empty queue is
 *     trivially "loaded"). Fading on the first `true` blinks the overlay out over
 *     a still-black globe — so we deliberately DON'T trust the first `true`.
 *   - `globe.tileLoadProgressEvent` fires with the remaining-tile count whenever
 *     the load queue changes. We drive off it: a COLD boot goes 0 → N (tiles
 *     started) → 0 (drained) ⇒ ready the instant it drains. A WARM boot (cached /
 *     nothing to fetch) can sit at ~0 the whole time; we confirm it STAYS drained
 *     for a short settle window and then call it ready, so a warm boot clears fast
 *     instead of waiting out the overlay's hard cap.
 *
 * requestRenderMode is OFF in MapView (continuous render), so tiles dispatch on
 * the rAF loop on their own — this watcher only OBSERVES, it never kicks a render
 * (the occlusion pump owns the hidden-window dispatch case).
 */

/** The slice of `Cesium.Event` this watcher touches. `addEventListener` returns a
 *  remover function (Cesium's contract) — structural so a test can fake it. */
export interface ImageryReadinessProgressEvent {
  addEventListener(listener: (queuedTileCount: number) => void): () => void;
}

/** The slice of `Cesium.Viewer` this watcher reads — structural for unit tests. */
export interface ImageryReadinessViewer {
  isDestroyed(): boolean;
  scene: {
    globe: {
      tilesLoaded: boolean;
      tileLoadProgressEvent: ImageryReadinessProgressEvent;
    };
  };
}

export interface ImageryReadinessHandle {
  /** True once readiness has fired. */
  isReady(): boolean;
  /** Detach the progress listener + cancel any pending settle timer. Idempotent. */
  destroy(): void;
}

export interface ImageryReadinessOptions {
  /** Warm-cache confirm window (ms): if the queue is drained but tiles never
   *  visibly loaded, wait this long still-drained before declaring ready. */
  settleMs?: number;
  /** Injected for tests; default setTimeout/clearTimeout. */
  schedule?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  cancel?: (handle: ReturnType<typeof setTimeout>) => void;
}

const DEFAULT_SETTLE_MS = 600;

export function installImageryReadiness(
  viewer: ImageryReadinessViewer,
  onReady: () => void,
  opts: ImageryReadinessOptions = {},
): ImageryReadinessHandle {
  const settleMs = opts.settleMs ?? DEFAULT_SETTLE_MS;
  const schedule = opts.schedule ?? ((fn, ms): ReturnType<typeof setTimeout> => setTimeout(fn, ms));
  const cancel = opts.cancel ?? ((h): void => clearTimeout(h));

  let ready = false;
  let destroyed = false;
  let sawTilesLoading = false;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let removeListener: () => void = () => undefined;

  const clearSettle = (): void => {
    if (settleTimer !== null) {
      cancel(settleTimer);
      settleTimer = null;
    }
  };

  const queueDrained = (): boolean =>
    !destroyed && !viewer.isDestroyed() && viewer.scene.globe.tilesLoaded;

  const fireReady = (): void => {
    if (ready || destroyed) return;
    ready = true;
    clearSettle();
    removeListener();
    onReady();
  };

  const onProgress = (queued: number): void => {
    if (ready || destroyed) return;
    if (queued > 0) {
      // Tiles are actively loading — remember it, and cancel any warm-settle so
      // the next drain is judged as a genuine "loaded then drained".
      sawTilesLoading = true;
      clearSettle();
      return;
    }
    // queued === 0 — the queue reports empty. Confirm the globe agrees.
    if (!queueDrained()) return;
    if (sawTilesLoading) {
      // Cold boot: tiles loaded and the queue drained ⇒ imagery is up. Ready now.
      fireReady();
      return;
    }
    // Warm / empty: never saw tiles load. This MIGHT be a frame-0 empty queue
    // with real tiles about to be requested, so don't fire immediately — confirm
    // the queue stays drained across a short window, then call it.
    if (settleTimer === null) {
      settleTimer = schedule(() => {
        settleTimer = null;
        if (!ready && !sawTilesLoading && queueDrained()) fireReady();
      }, settleMs);
    }
  };

  removeListener = viewer.scene.globe.tileLoadProgressEvent.addEventListener(onProgress);

  return {
    isReady: (): boolean => ready,
    destroy: (): void => {
      if (destroyed) return;
      destroyed = true;
      clearSettle();
      removeListener();
    },
  };
}
